import { createTRPCReact } from "@trpc/react-query";
import { httpBatchLink } from "@trpc/client";
import type { inferRouterInputs, inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "../../../../services/api/src/trpc/router";
import { getAuthToken, getRefreshFunction } from "./axios";

export const trpc = createTRPCReact<AppRouter>();

/** Procedure input types inferred from the API router — derive client types from these, don't hand-duplicate. */
export type RouterInputs = inferRouterInputs<AppRouter>;

/** Procedure output types inferred from the API router — derive client types from these, don't hand-duplicate. */
export type RouterOutputs = inferRouterOutputs<AppRouter>;

// Global error handler for tRPC errors (set by AuthContext)
let globalTrpcErrorHandler: ((message: string) => void) | null = null;

export const setTrpcErrorHandler = (fn: ((message: string) => void) | null) => {
  globalTrpcErrorHandler = fn;
};

// Simplified fetch wrapper - refresh handling now delegated to global refresh manager
export async function authenticatedFetch(
  input: string | globalThis.URL | globalThis.Request,
  init?: globalThis.RequestInit,
): Promise<globalThis.Response> {
  // Add Authorization header if token exists
  const token = getAuthToken();
  const headers = new globalThis.Headers(init?.headers);

  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  // Preserve credentials for endpoints such as anonymous share sessions that
  // intentionally use cookies. Authentication itself uses bearer tokens.
  const requestOptions: globalThis.RequestInit = {
    ...init,
    headers,
    credentials: "include", // Include cookies for refresh token
  };

  let response: globalThis.Response;
  try {
    response = await globalThis.fetch(input, requestOptions);
  } catch (fetchError) {
    // Network error - show user-friendly message
    if (globalTrpcErrorHandler) {
      globalTrpcErrorHandler("Network error. Please check your connection and try again.");
    }
    throw fetchError;
  }

  // Handle rate limiting gracefully
  if (response.status === 429) {
    if (globalTrpcErrorHandler) {
      globalTrpcErrorHandler("Too many requests. Please wait a moment and try again.");
    }
  }

  // Handle server errors gracefully
  if (response.status >= 500) {
    if (globalTrpcErrorHandler) {
      globalTrpcErrorHandler("Server error. Please try again later.");
    }
  }

  if (response.status === 401) {
    // A public/logged-out request has no refresh authority. Replaying it only
    // creates a 401 loop and can stampede global navigation queries.
    if (!token) return response;
    const refresh = getRefreshFunction();
    if (!refresh) return response;

    try {
      await refresh();
    } catch {
      return response;
    }
    const retryHeaders = new globalThis.Headers(requestOptions.headers);
    const refreshedToken = getAuthToken();
    if (!refreshedToken || refreshedToken === token) return response;
    retryHeaders.set("Authorization", `Bearer ${refreshedToken}`);
    return globalThis.fetch(input, { ...requestOptions, headers: retryHeaders });
  }

  return response;
}

export const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: `${import.meta.env.VITE_API_BASE_URL}/trpc`,
      fetch: authenticatedFetch,
      // Use POST to avoid URL length limits with batched queries
      methodOverride: "POST",
    }),
  ],
});
